import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Heading,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SortHeader from "../components/SortHeader";
import ResizeHandle from "../components/ResizeHandle";
import AddWorkModal from "../components/AddWorkModal";
import UploadWorksModal from "../components/UploadWorksModal";
import ExportWorksModal from "../components/ExportWorksModal";
import BulkActionBar from "../components/BulkActionBar";
import BulkEditWorksModal from "../components/BulkEditWorksModal";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import {
  CERTIFICATION_STATUSES,
  UNCERTIFIED_STATS_KEY,
  statusColor,
} from "../utils/certStatus";
import { fmtDate } from "../utils/datetime";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  WorksApi,
  type SortDir,
  type WorkListResponse,
  type WorkSortField,
  type WorkStats,
} from "../api/works";
import BooleanSearchHelp from "../components/BooleanSearchHelp";
import Pagination from "../components/Pagination";
import StatsPanel from "../components/StatsPanel";
import logger from "../analytics/logger";
import { useBasicSearchFlag } from "../analytics/featureFlags";
import { readSort, writeSort, SORT_KEYS } from "../lib/tableSort";

type Form = {
  type: string;
  format: string;
  status: string[];
  profile: string;
  created_after: string;
  created_before: string;
  expr: string;
};

const EMPTY_FORM: Form = {
  type: "",
  format: "",
  status: [],
  profile: "",
  created_after: "",
  created_before: "",
  expr: "",
};

function parseStatusList(v: string | null): string[] {
  return (v ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function formFromParams(p: URLSearchParams): Form {
  return {
    type: p.get("type") ?? "",
    format: p.get("format") ?? "",
    status: parseStatusList(p.get("status")),
    profile: p.get("profile") ?? "",
    created_after: p.get("created_after") ?? "",
    created_before: p.get("created_before") ?? "",
    // Back-compat: surface a bookmarked `?q=…` in the unified expression
    // input. The page never emits `?q=` itself after this change.
    expr: p.get("expr") ?? p.get("q") ?? "",
  };
}

const PREFS_KEY = "support_v2_works_table_v1";
const COL_MIN = 60;
const COL_MAX = 600;

type ColField = WorkSortField | "profiles" | "status";

const DEFAULT_WIDTHS: Record<ColField, number> = {
  created_at: 140,
  title: 320,
  status: 130,
  type: 140,
  format: 140,
  profiles: 220,
};

type TablePrefs = {
  widths: Partial<Record<ColField, number>>;
  sort?: WorkSortField;
  dir?: SortDir;
};

function loadPrefs(): TablePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { widths: {} };
    const parsed = JSON.parse(raw);
    return { widths: parsed.widths ?? {}, sort: parsed.sort, dir: parsed.dir };
  } catch {
    return { widths: {} };
  }
}

function savePrefs(p: TablePrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // quota or disabled — non-fatal
  }
}

const MODE_KEY = "support_v2_works_search_mode_v1";
type SearchMode = "basic" | "advanced";

function loadMode(): SearchMode {
  try {
    return localStorage.getItem(MODE_KEY) === "advanced" ? "advanced" : "basic";
  } catch {
    return "basic";
  }
}

function saveMode(m: SearchMode) {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    // quota or disabled — non-fatal
  }
}

function paramsHaveAdvanced(p: URLSearchParams): boolean {
  return (
    !!p.get("type") ||
    !!p.get("format") ||
    !!p.get("status") ||
    !!p.get("profile") ||
    !!p.get("created_after") ||
    !!p.get("created_before")
  );
}

export default function Works() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const pageSize = Math.max(1, parseInt(params.get("page_size") ?? "40", 10) || 40);

  const [draft, setDraft] = useState<Form>(() => formFromParams(params));
  const basicSearchEnabled = useBasicSearchFlag();
  const [mode, setMode] = useState<SearchMode>(() =>
    paramsHaveAdvanced(params) ? "advanced" : loadMode(),
  );
  useEffect(() => {
    saveMode(mode);
  }, [mode]);
  const [types, setTypes] = useState<string[]>([]);
  const [formats, setFormats] = useState<string[]>([]);
  const [stats, setStats] = useState<WorkStats | null>(null);
  const [data, setData] = useState<WorkListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const confirm = useDisclosure();
  const addDisclosure = useDisclosure();
  const uploadDisclosure = useDisclosure();
  const exportDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    WorksApi.types().then(setTypes).catch((e) => logger.error("works_types_failed", e));
    WorksApi.formats().then(setFormats).catch((e) => logger.error("works_formats_failed", e));
    WorksApi.stats().then(setStats).catch((e) => logger.error("works_stats_failed", e));
  }, []);

  // Selection is cleared on each successful load — it is only meaningful for the
  // rows currently visible. refetchTick is bumped after bulk-delete.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await WorksApi.list({
          page,
          page_size: pageSize,
          type: params.get("type") ?? undefined,
          format: params.get("format") ?? undefined,
          status: params.get("status") ?? undefined,
          profile: params.get("profile") ?? undefined,
          created_after: params.get("created_after") ?? undefined,
          created_before: params.get("created_before") ?? undefined,
          expr: params.get("expr") ?? undefined,
          sort: (params.get("sort") as WorkSortField | null) ?? undefined,
          dir: (params.get("dir") as SortDir | null) ?? undefined,
        });
        if (!active) return;
        setData(d);
        setSelectedIds(new Set());
      } catch (e) {
        if (!active) return;
        logger.error("works_list_failed", e);
        const data =
          (e as { response?: { data?: { error?: string; at?: number; message?: string } } })
            ?.response?.data ?? {};
        if (data.error === "expr_parse_error") {
          const at = typeof data.at === "number" ? ` (at position ${data.at})` : "";
          setError(`Expression error: ${data.message || "invalid expression"}${at}`);
        } else {
          setError(data.error ?? "Failed to load works");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [params, refetchTick, page, pageSize]);

  const visibleIds = data?.works.map((w) => w.id) ?? [];
  const selectedOnPage = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPage === visibleIds.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleAllOnPage = (checked: boolean) =>
    setSelectedIds(() => (checked ? new Set(visibleIds) : new Set()));
  const toggleOne = (id: string, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await WorksApi.bulkDelete(ids);
      toast({
        title: `Deleted ${res.deleted.length} work${res.deleted.length === 1 ? "" : "s"}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setSelectedIds(new Set());
      setRefetchTick((t) => t + 1);
      confirm.onClose();
    } catch (e) {
      logger.error("works_bulk_delete_failed", e);
      toast({
        title: "Delete failed",
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const refresh = () => {
    const next = new URLSearchParams();
    if (draft.type) next.set("type", draft.type);
    if (draft.format) next.set("format", draft.format);
    if (draft.status.length) next.set("status", draft.status.join(","));
    if (draft.profile.trim()) next.set("profile", draft.profile.trim());
    if (draft.created_after) next.set("created_after", draft.created_after);
    if (draft.created_before) next.set("created_before", draft.created_before);
    if (draft.expr.trim()) next.set("expr", draft.expr.trim());
    next.set("page", "1");
    setParams(next);
  };

  const reset = () => {
    setDraft(EMPTY_FORM);
    setParams(new URLSearchParams({ page: "1" }));
  };

  const toggleMode = () => {
    if (mode === "advanced") {
      setDraft({
        ...draft,
        type: "",
        format: "",
        status: [],
        profile: "",
        created_after: "",
        created_before: "",
      });
      const next = new URLSearchParams();
      if (draft.expr) next.set("expr", draft.expr);
      const sortParam = params.get("sort");
      const dirParam = params.get("dir");
      if (sortParam) next.set("sort", sortParam);
      if (dirParam) next.set("dir", dirParam);
      next.set("page", "1");
      setParams(next);
      setMode("basic");
    } else {
      setMode("advanced");
    }
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.set("page", "1");
    setParams(next);
  };

  const [prefs, setPrefs] = useState<TablePrefs>(() => loadPrefs());

  // On mount only: if URL has no sort/dir, seed it from the remembered
  // sessionStorage sort (defaults to created_at desc if nothing remembered).
  useEffect(() => {
    if (!params.get("sort")) {
      const remembered = readSort(SORT_KEYS.works);
      const next = new URLSearchParams(params);
      next.set("sort", remembered.sort);
      next.set("dir", remembered.dir);
      setParams(next, { replace: true });
    }
    // Intentionally mount-only: re-seeding on every params change would fight
    // the user's own sort clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remembered = readSort(SORT_KEYS.works);
  const sort = (params.get("sort") as WorkSortField | null) ?? (remembered.sort as WorkSortField);
  const dir = (params.get("dir") as SortDir | null) ?? remembered.dir;

  // Persist the active sort to sessionStorage (source of truth on next mount).
  // We no longer mirror sort/dir into React state here (nothing reads them, and
  // set-state-in-effect flags it); column widths still live in prefs.
  useEffect(() => {
    writeSort(SORT_KEYS.works, { sort, dir });
  }, [sort, dir]);

  const widthOf = (f: ColField) => prefs.widths[f] ?? DEFAULT_WIDTHS[f];

  const setWidth = useCallback((f: ColField, w: number) => {
    setPrefs((p) => {
      const next: TablePrefs = {
        ...p,
        widths: { ...p.widths, [f]: Math.max(COL_MIN, Math.min(COL_MAX, Math.round(w))) },
      };
      savePrefs(next);
      return next;
    });
  }, []);

  const toggleSort = (field: WorkSortField) => {
    const next = new URLSearchParams(params);
    if (sort === field) {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    } else {
      next.set("sort", field);
      next.set("dir", field === "created_at" ? "desc" : "asc");
    }
    next.set("page", "1");
    setParams(next);
  };

  const resizeProps = (field: ColField) => ({
    width: widthOf(field),
    onResize: (w: number) => setWidth(field, w),
    onResetWidth: () => setWidth(field, DEFAULT_WIDTHS[field]),
  });
  const sortHeaderProps = (field: WorkSortField) => ({
    active: sort === field,
    dir,
    onToggle: () => toggleSort(field),
    ...resizeProps(field),
  });

  // Only offer statuses at least one work actually holds. Counts come from
  // /works/stats, which buckets on each work's LATEST certification across the
  // whole table (independent of the current filters). Falls back to the full
  // list until stats land, and always keeps an already-checked status visible
  // so a bookmarked `?status=` stays togglable. This is what keeps env-specific
  // states — NEEDS_REVIEW exists in prod but not dev — from cluttering either.
  const statusOptions = CERTIFICATION_STATUSES.filter((s) => {
    if (!stats || draft.status.includes(s)) return true;
    const key = s === "UNCERTIFIED" ? UNCERTIFIED_STATS_KEY : s;
    return (stats.by_status[key] ?? 0) > 0;
  });

  return (
    <Box p={6}>
      <HStack justify="space-between" align="center" mb={4}>
        <Heading size="lg">Works</Heading>
        <HStack spacing={2}>
          <Button colorScheme="blue" onClick={addDisclosure.onOpen}>
            Add work
          </Button>
          <Button colorScheme="blue" onClick={uploadDisclosure.onOpen}>
            Bulk import
          </Button>
          <Button colorScheme="blue" onClick={exportDisclosure.onOpen}>
            Export
          </Button>
        </HStack>
      </HStack>

      <AddWorkModal
        isOpen={addDisclosure.isOpen}
        onClose={addDisclosure.onClose}
        onCreated={() => setRefetchTick((t) => t + 1)}
      />

      <UploadWorksModal
        isOpen={uploadDisclosure.isOpen}
        onClose={uploadDisclosure.onClose}
        onImported={() => setRefetchTick((t) => t + 1)}
      />

      <ExportWorksModal
        isOpen={exportDisclosure.isOpen}
        onClose={exportDisclosure.onClose}
        params={params}
      />

      <BulkEditWorksModal
        isOpen={editDisclosure.isOpen}
        onClose={editDisclosure.onClose}
        ids={[...selectedIds]}
        formatOptions={formats}
        onSaved={() => {
          setSelectedIds(new Set());
          setRefetchTick((t) => t + 1);
        }}
      />

      {!basicSearchEnabled && stats && (
        <StatsPanel
          sections={[
            {
              title: "By status (latest cert)",
              entries: Object.entries(stats.by_status).map(([k, v]) => ({ label: k, value: v })),
            },
            {
              title: "Approved by type",
              entries: Object.entries(stats.by_type_approved).map(([k, v]) => ({
                label: k,
                value: v,
              })),
            },
            {
              title: "Recently added",
              entries: [
                { label: "Past 24h", value: stats.added.day },
                { label: "Past 7d", value: stats.added.week },
                { label: "Past 30d", value: stats.added.month },
              ],
            },
          ]}
        />
      )}

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        {(!basicSearchEnabled || mode === "advanced") && (
        <HStack spacing={3} wrap="wrap" mb={3}>
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              fontWeight="normal"
              rightIcon={<ChevronDownIcon />}
              maxW="240px"
              textAlign="left"
            >
              {draft.status.length === 0
                ? "All statuses"
                : draft.status.length === 1
                  ? draft.status[0]
                  : `${draft.status.length} statuses`}
            </MenuButton>
            <MenuList maxH="320px" overflowY="auto" minW="220px">
              <MenuItem
                onClick={() => setDraft({ ...draft, status: [] })}
                fontStyle="italic"
                color="gray.600"
              >
                Clear all
              </MenuItem>
              <Stack spacing={1} px={3} py={2}>
                {statusOptions.map((s) => (
                  <Checkbox
                    key={s}
                    size="sm"
                    isChecked={draft.status.includes(s)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        status: e.target.checked
                          ? [...draft.status, s]
                          : draft.status.filter((x) => x !== s),
                      })
                    }
                  >
                    {s}
                  </Checkbox>
                ))}
              </Stack>
            </MenuList>
          </Menu>
          <Select
            size="sm"
            placeholder="All types"
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            maxW="220px"
          >
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <Select
            size="sm"
            placeholder="All formats"
            value={draft.format}
            onChange={(e) => setDraft({ ...draft, format: e.target.value })}
            maxW="220px"
          >
            {formats.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
          <Input
            size="sm"
            maxW="220px"
            placeholder="Profile contains…"
            value={draft.profile}
            onChange={(e) => setDraft({ ...draft, profile: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <HStack spacing={1}>
            <Input
              size="sm"
              type="date"
              value={draft.created_after}
              onChange={(e) => setDraft({ ...draft, created_after: e.target.value })}
              maxW="160px"
              title="Created on or after"
              aria-label="Created on or after"
            />
            <Box as="span" color="gray.500" fontSize="sm">
              –
            </Box>
            <Input
              size="sm"
              type="date"
              value={draft.created_before}
              onChange={(e) => setDraft({ ...draft, created_before: e.target.value })}
              maxW="160px"
              title="Created on or before"
              aria-label="Created on or before"
            />
          </HStack>
        </HStack>
        )}
        <HStack spacing={3} wrap="wrap">
          <Input
            size="sm"
            flex="1"
            minW="320px"
            placeholder={`Search… (or use field expressions like creator = "*foo")`}
            value={draft.expr}
            onChange={(e) => setDraft({ ...draft, expr: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <BooleanSearchHelp
            freeTextScope="work title and description"
            fields="creator, work"
            examples={[
              "eagle tree",
              "creator = 'ron*'",
              "creator = '*eagle' or eagle",
              "work = 'eagle*'",
              '"exact phrase"',
            ]}
            footer="creator and work are cross-entity predicates — same scope on both pages."
          />
          <Button size="sm" colorScheme="blue" onClick={refresh}>
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </HStack>
        <HStack mt={2} minH="24px" justify="space-between" align="center">
          <Box>
            {basicSearchEnabled && (
              <Button variant="link" size="sm" onClick={toggleMode}>
                {mode === "basic" ? "Advanced search" : "Basic search"}
              </Button>
            )}
          </Box>
          <BulkActionBar
            count={selectedIds.size}
            onEdit={editDisclosure.onOpen}
            onDelete={confirm.onOpen}
            entity="work"
          />
        </HStack>
      </Box>

      <ConfirmDeleteDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onConfirm={doBulkDelete}
        count={selectedIds.size}
        entity="work"
        busy={deleting}
      />

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={10} textAlign="center">
          <Spinner />
        </Box>
      ) : data ? (
        <>
          <Box overflowX="auto">
            <Table
              size="sm"
              variant="simple"
              sx={{ tableLayout: "fixed", width: "max-content" }}
            >
              <colgroup>
                <col style={{ width: "36px" }} />
                <col style={{ width: `${widthOf("created_at")}px` }} />
                <col style={{ width: `${widthOf("title")}px` }} />
                <col style={{ width: `${widthOf("status")}px` }} />
                <col style={{ width: `${widthOf("type")}px` }} />
                <col style={{ width: `${widthOf("format")}px` }} />
                <col style={{ width: `${widthOf("profiles")}px` }} />
              </colgroup>
              <Thead>
                <Tr>
                  <Th width="36px" px={2}>
                    <Checkbox
                      isChecked={allOnPageSelected}
                      isIndeterminate={someOnPageSelected}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                      aria-label="Select all on this page"
                    />
                  </Th>
                  <SortHeader label="Created" {...sortHeaderProps("created_at")} />
                  <SortHeader label="Title" {...sortHeaderProps("title")} />
                  <Th position="relative" width={`${widthOf("status")}px`}>
                    Status
                    <ResizeHandle {...resizeProps("status")} />
                  </Th>
                  <SortHeader label="Type" {...sortHeaderProps("type")} />
                  <SortHeader label="Format" {...sortHeaderProps("format")} />
                  <Th position="relative" width={`${widthOf("profiles")}px`}>
                    Profiles
                    <ResizeHandle {...resizeProps("profiles")} />
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.works.map((w) => (
                  <Tr
                    key={w.id}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => navigate(`/works/${w.id}`)}
                  >
                    <Td px={2} onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        isChecked={selectedIds.has(w.id)}
                        onChange={(e) => toggleOne(w.id, e.target.checked)}
                        aria-label={`Select work ${w.title}`}
                      />
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {fmtDate(w.created_at)}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {w.title}
                    </Td>
                    <Td>
                      <Badge colorScheme={statusColor(w.status)} textTransform="none">
                        {w.status}
                      </Badge>
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {w.type}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {w.format}
                    </Td>
                    <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {w.profiles.join(", ") || "—"}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}
    </Box>
  );
}
